/**
 * Тексты черновика, экрана ревью и конфликтов (§6, §11.6, §12).
 *
 * Общий словарь `web/src/texts.ts` трогать нельзя — его читают другие волны,
 * поэтому §12.1 (кнопки) и §6.2 (поповер чипа) берутся оттуда как есть, а
 * здесь живёт только то, чего там нет: подписи дока, шапка ревью, группы
 * правок, дословные формулировки конфликтов (§12.5) и тосты публикации (§12.6).
 */
import { countOf, forms, plural } from '../../texts.js'

const dayForms = ['день', 'дня', 'дней'] as const
/** §6.6 просит именно «неподтверждённых правок», а не общее «правок». */
const pendingForms = ['неподтверждённая правка', 'неподтверждённые правки', 'неподтверждённых правок'] as const

/** «14.10 в 11:28» — единый формат момента во всех текстах раздела. */
export function formatMoment(iso: string | null): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const day = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' }).format(date)
  const time = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date)
  return `${day} в ${time}`
}

/** «13 октября» — для плашки возврата к черновику. */
export function formatDay(iso: string | null): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(date)
}

/** Сколько суток черновику: от этого зависит плашка §6.6. */
export function daysSince(iso: string | null): number {
  if (!iso) return 0
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 0
  return Math.floor((Date.now() - date.getTime()) / 86_400_000)
}

export const draftTexts = {
  /** §6.1 Док черновика */
  dock: {
    region: 'Черновик',
    label: 'Черновик',
    saved: 'сохранено',
    failed: 'Не сохранилось',
    undoLast: '← Шаг назад',
    undoHint: 'Отменить последнюю правку (⌘Z). Массовая простановка снимается целиком.',
    cancelAll: '⤺ Отменить всё',
    /** §12.1 `commit` разбит на две части: счётчик роллится отдельно (§6.1). */
    commitText: 'Подтвердить изменения',
    hotkeyMac: '⌘↵',
    hotkeyOther: 'Ctrl+↵',
    commitAria: (n: number) => `Подтвердить изменения: ${countOf(n, forms.change)}. Откроется экран ревью`,
  },

  /** §6.6 Плашка возврата к черновику */
  returnBanner: {
    text: (changes: number, day: string | null) => {
      const head = `У вас ${changes} ${plural(changes, pendingForms)}`
      return day ? `${head} с ${day}` : head
    },
    showOnlyMine: 'Показать только их',
    close: 'Скрыть плашку',
    region: 'Неподтверждённые правки',
  },

  /** §6.2 Чип черновика */
  chip: {
    aria: (n: number) => `Черновик: ${countOf(n, forms.change)}. Открыть список`,
    text: (n: number) => `Черновик · ${n}`,
    newBrand: 'новый бренд',
    newBrandMark: '🆕',
  },

  /** §6.3 Экран ревью */
  review: {
    changes: (n: number) => countOf(n, forms.change),
    brands: (n: number) => countOf(n, forms.brand),
    startedAt: (moment: string) => `черновик начат ${moment}`,
    bothChannels: 'Розница и Опт',
    groupNewBrands: '🆕 Новые бренды',
    groupOperation: 'Массовая правка',
    groupService: 'Архивация и отвязки',
    groupOther: 'Прочее',
    operationExpand: 'Показать бренды',
    operationCollapse: 'Свернуть список',
    categoriesPrefix: 'категории',
    tierPrefix: 'тир',
    visibilityPrefix: 'видимость',
    seoPrefix: 'SEO',
    contextPrefix: 'контекст',
    urlPrefix: 'URL',
    brandAdded: 'добавлен',
    /* Закрытая задача — одна строка: что сделано и что об этом написано. */
    taskDone: (what: string) => `Выполнена задача «${what}»`,
    taskReopened: (what: string) => `Задача «${what}» снова открыта`,
    taskReport: (text: string) => `отчёт: ${text}`,
    taskFallback: 'задача',
    empty: 'пусто',
    noteLabel: 'Комментарий к пакету (необязательно, увидят все в чате и в журнале)',
    removedPrefix: 'Убрано:',
    removeAria: (title: string) => `Убрать правку: ${title}`,
    linesAria: 'Правки черновика',
    lineAria: (title: string, from: string, to: string) => `${title}: было ${from}, станет ${to}`,
    was: 'было',
    becomes: 'станет',
    keys: 'j и k — по правкам, x — убрать правку под фокусом, Ctrl+Enter — опубликовать',
    staleDraft: (days: number) =>
      `Черновику ${days} ${plural(days, dayForms)}. Посмотрите, что в нём, — часть значений могла измениться.`,
    emptyTitle: 'В черновике ничего нет',
    emptyText: 'Поправьте что-нибудь в таблице — правки появятся здесь',
    publishing: 'Публикуем…',
  },

  /** §6.4 и §12.5 Конфликты — формулировки дословные */
  conflict: {
    heading: (n: number) => `▲ ${countOf(n, forms.conflict)} — решите, чтобы опубликовать`,
    wasAtStart: 'Когда вы начинали, было:',
    youChangeTo: 'Вы меняете на:',
    nowInData: 'Сейчас в данных:',
    source: (author: string, moment: string | null, changeSetId: number | null) =>
      [author, moment, changeSetId === null ? null : `пакет #${changeSetId}`]
        .filter((part): part is string => Boolean(part))
        .join(', '),
    unknownAuthor: 'другой пользователь',
    keepMine: (value: string) => `Оставить моё: ${value}`,
    takeTheirs: (value: string) => `Принять текущее: ${value}`,
    dropChange: 'Убрать правку',
    keepAllMine: 'Оставить все мои',
    takeAllTheirs: 'Принять все текущие',
    groupAria: (title: string) => `Решение по конфликту: ${title}`,
  },

  /** §6.3 Предпросмотр сообщения в Telegram. Текст собирает сервер. */
  telegram: {
    toggle: 'Что увидит команда в Telegram',
    aria: 'Предпросмотр сообщения в Telegram',
    loading: 'Собираем сообщение…',
    nothing: 'По этим правкам в чат ничего не уйдёт.',
    nextMessage: '— следующее сообщение —',
    numberFixed: 'Номер пакета уже закреплён за черновиком — при публикации он не изменится.',
    failed: 'Предпросмотр не загрузился. Публикации это не мешает.',
    disabled: 'Уведомления в Telegram выключены в настройках — сообщение не уйдёт.',
  },

  /** §6.1 и §6.3 Отмена всего черновика — через модалку, необратимо */
  discard: {
    title: (n: number) => `Отменить ${countOf(n, forms.change)}?`,
    body: 'Это нельзя вернуть.',
    note: 'Значения вернутся к опубликованным. Пакет в историю не попадёт.',
    reviewFirst: 'Сначала посмотреть ревью',
    confirm: 'Отменить черновик',
    keep: 'Оставить черновик',
  },

  /** §12.6 Тосты публикации */
  toast: {
    published: (changeSetId: number, changes: number, telegram: boolean) =>
      telegram
        ? `Пакет #${changeSetId} опубликован · ${countOf(changes, forms.change)} · отправлено в Telegram`
        : `Пакет #${changeSetId} опубликован · ${countOf(changes, forms.change)}`,
    publishedNoTelegram: (changeSetId: number) =>
      `Пакет #${changeSetId} опубликован. Сообщение в чат пока не ушло, повторим автоматически.`,
    /** 200 с appliedCount: 0 — спокойная констатация, а не ошибка (§6.4). */
    nothingApplied:
      'Публиковать было нечего: все спорные правки вы оставили за коллегами. Черновик закрыт.',
    publishFailed: (message: string) =>
      `Не удалось подтвердить пакет: ${message}. Черновик на месте, попробуйте ещё раз`,
    conflicts: (n: number) =>
      `Пока вы работали, ${countOf(n, forms.conflict)}: решите, что оставить`,
  },
} as const
